import api from './api';
import { isPrivateResult, schedulePrivateResultDelete } from '../utils/privateResults';

const imageGenerationService = {
    // Generate AI-transformed image
    generateImage: async (formData) => {
        const response = await api.post('/api/image-generation/generate', formData, {
            timeout: 120000 // 2 minutes timeout for file upload
        });
        return response;
    },

    // Get user's image generation history
    getHistory: async (params = {}) => {
        const response = await api.get('/api/image-generation/history', {
            params: params
        });
        return response;
    },

    // Get image metadata by ID
    getImageById: async (id) => {
        const response = await api.get(`/api/image-generation/${id}`);
        return response;
    },

    // Distinct background + dress-code values for the AI-photo-edit filter dropdowns.
    getFilterOptions: async (params = {}) => {
        const response = await api.get('/api/image-generation/filter-options', { params });
        return response;
    },

    // Download image (type: 'ai' or 'original')
    downloadImage: async (id, type = 'ai') => {
        const response = await api.get(`/api/image-generation/download/${id}`, {
            params: { type },
            responseType: 'blob'
        });
        return response;
    },

    // Delete image from history
    deleteImage: async (id) => {
        const response = await api.delete(`/api/image-generation/delete/${id}`);
        return response;
    },

    // Update image name
    updateImageName: async (id, fileName) => {
        const response = await api.patch(`/api/image-generation/${id}`, { fileName });
        return response;
    },

    // Update only the background token of a transparent-subject image (hex or 'transparent').
    // No AI re-generation, no credit — the stored cut-out is untouched.
    updateBackground: async (id, background) => {
        const response = await api.patch(`/api/image-generation/${id}/background`, { background });
        return response;
    },

    // Track an image download (independent of actual blob retrieval)
    trackDownload: async (id) => {
        // An image made from a customer's PRIVATE booth file is deleted shortly after it is first downloaded / printed
        // (utils/privateResults.js) — decided here, whether or not the tracking call itself succeeds.
        if (isPrivateResult(id)) schedulePrivateResultDelete(id);
        const response = await api.patch(`/api/image-generation/track-download/${id}`);
        return response;
    },

    // Track an image print action
    trackPrint: async (id) => {
        if (isPrivateResult(id)) schedulePrivateResultDelete(id);
        const response = await api.patch(`/api/image-generation/track-print/${id}`);
        return response;
    },

    // Submit feedback for a generated image. `feedback` is 'positive' | 'negative';
    // a negative rating may include a predefined `reason` key and free-text `note`.
    submitFeedback: async (id, feedback, reason = null, note = null) => {
        const body = { feedback };
        if (reason) body.reason = reason;
        if (note) body.note = note;
        const response = await api.patch(`/api/image-generation/feedback/${id}`, body);
        return response;
    },

    // Replace the R2 AI image with a watermarked version
    replaceAiImage: async (id, formData) => {
        const response = await api.patch(`/api/image-generation/${id}/ai-image`, formData);
        return response;
    },

    // Save a manually edited image (no AI) — creates a new record
    saveManualEdit: async (formData) => {
        const response = await api.post('/api/manual-edit/save', formData, {
            timeout: 60000,
        });
        return response;
    },

    // Update an existing manually edited image record (no new record created)
    updateManualEdit: async (id, formData) => {
        const response = await api.put(`/api/manual-edit/update/${id}`, formData, {
            timeout: 60000,
        });
        return response;
    },

    // Deduct credit for manual background removal (reads creditCost_IMAGE_MANUAL from settings)
    deductManualBgCredit: async ({ officeSpaceId } = {}) => {
        const params = officeSpaceId ? `?officeSpaceId=${officeSpaceId}` : '';
        const response = await api.post(`/api/manual-edit/deduct-bg${params}`);
        return response;
    },
};

export default imageGenerationService;
